import { apiClient, ApiError } from "@nabble/sdk";
import { Alert, AuthHeader, AuthLayout, Button, Field, PasswordInput } from "@nabble/ui";
import { type FormEvent, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../auth/AuthContext";
import { destinationAfterStep, type AuthStep } from "../../../auth/session";
import { PasswordChecklist } from "../../../components/PasswordChecklist";
import { passwordChecks } from "../../../components/PasswordPolicy";
import { PasswordStrength } from "../../../components/PasswordStrength";
import { PasswordViolationsAlert } from "../../../components/PasswordViolationsAlert";
import { errorMessage } from "../../../errors";

/** Used both for /invite?token= (first password) and /reset-password?token=. */
export default function SetPasswordPage(): React.JSX.Element {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const isReset = useLocation().pathname.startsWith("/reset-password");
  const navigate = useNavigate();
  const { applyNext } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invalidLink, setInvalidLink] = useState(!token);
  const [violations, setViolations] = useState<string[]>([]);
  const [touchedConfirm, setTouchedConfirm] = useState(false);

  const checks = passwordChecks(password, confirm);
  const valid = checks.every((c) => c.ok);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setViolations([]);
    if (!valid) {
      setTouchedConfirm(true);
      return;
    }
    setLoading(true);
    try {
      const { next } = await apiClient<{ next: AuthStep }>("/api/auth/password", {
        method: "POST",
        body: { token, password },
      });
      applyNext(next);
      navigate(destinationAfterStep(next));
    } catch (err) {
      if (err instanceof ApiError && err.code === "weak_password" && err.body.violations) {
        setViolations(err.body.violations);
      } else if (err instanceof ApiError && err.code === "invalid_link") {
        setInvalidLink(true);
      } else {
        setError(errorMessage(err, "We couldn't save the password. Please try again."));
      }
    } finally {
      setLoading(false);
    }
  }

  if (invalidLink) {
    return (
      <AuthLayout>
        <AuthHeader
          icon="alert"
          iconTone="warning"
          title="This link isn't valid"
          lead="It may have already been used or it may have expired. Links work only once and for a limited time."
        />
        <div className="nb-auth-content">
          <Link to="/forgot-password" className="nb-btn nb-btn-primary nb-btn-lg nb-btn-block">
            Ask for a new link
          </Link>
        </div>
        <div className="nb-auth-foot">
          <Link to="/login">Back to sign in</Link>
        </div>
      </AuthLayout>
    );
  }

  const mismatch = touchedConfirm && confirm.length > 0 && confirm !== password;

  return (
    <AuthLayout step={isReset ? undefined : { current: 1, total: 4, label: "password" }}>
      <AuthHeader
        title={isReset ? "Reset your password" : "Create your password"}
        lead={isReset ? "Choose a new password for your account." : "Welcome to Nabble Suite. Choose a strong password for your account."}
      />
      <form className="nb-auth-content" onSubmit={(e) => void submit(e)} noValidate>
        <Field label="New password">
          <PasswordInput autoComplete="new-password" required autoFocus value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <PasswordStrength password={password} />
        <Field label="Confirm the password" error={mismatch ? "The passwords don't match." : undefined}>
          <PasswordInput
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            onBlur={() => setTouchedConfirm(true)}
          />
        </Field>
        <PasswordChecklist checks={checks} />
        {violations.length > 0 ? (
          <PasswordViolationsAlert title="The password doesn't meet the security policy" violations={violations} />
        ) : error ? (
          <Alert tone="danger">{error}</Alert>
        ) : null}
        <Button type="submit" label="Save and continue" size="lg" block loading={loading} />
      </form>
    </AuthLayout>
  );
}
